import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { calculateWeightPenalty } from "./rbmCalculations";

const ConfirmEvaluationDialog = ({
  open,
  onClose,
  onSubmit,
  report,
  formData,
  disabled,
}) => {
  const [previewScore, setPreviewScore] = useState(null);

  const previewTints = useMemo(() => {
    if (!previewScore) {
      return {
        scoreColor: "inherit",
        barColor: "primary.main",
        trackColor: "grey.200",
      };
    }
    const exp = Number(previewScore.expected ?? 0);
    const scoreColor =
      exp === 0 ? "error.main" : exp < 50 ? "warning.dark" : "inherit";
    const barColor =
      exp === 0 ? "error.main" : exp < 50 ? "warning.main" : "primary.main";
    const trackColor =
      exp === 0 ? "error.light" : exp < 50 ? "warning.light" : "grey.200";
    return { scoreColor, barColor, trackColor };
  }, [previewScore?.expected]);

  useEffect(() => {
    if (open && report && formData) {
      const computePreview = () => {
        const { achievedTarget, quality, completed } = formData;
        const { targetShare, measurementUnitCode, weight } = report;

        const target = Number(targetShare || 100);
        const achieved = Number(
          targetShare > 0 ? achievedTarget : completed === "yes" ? 100 : 0 || 0
        );
        const weightVal = Number(weight || 1);

        const { ratio, baseScore, weightedScore, expected, qualityAdjusted } =
          calculateWeightPenalty({
            target,
            achieved,
            weight: weightVal,
            measurementUnitCode,
            quality,
          });

        setPreviewScore({
          baseScore,
          weightedScore,
          qualityAdjusted,
          expected,
          ratio,
          fields: {
            target,
            achieved,
            measurementUnit: report.measurementUnit,
            weight: weightVal,
          },
        });
      };
      computePreview();
    }
  }, [open, report, formData]);

  if (!previewScore) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Confirm Evaluation</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Left Summary */}
          <Grid item xs={12} md={6}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell width="40%">
                      <strong>Employee</strong>
                    </TableCell>
                    <TableCell>{report.employeeNames}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Activity</strong>
                    </TableCell>
                    <TableCell>{report.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Target</strong>
                    </TableCell>
                    <TableCell>
                      {previewScore.fields.target}{" "}
                      {previewScore.fields.measurementUnit}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Achieved</strong>
                    </TableCell>
                    <TableCell sx={{ color: previewTints.scoreColor }}>
                      {previewScore.fields.achieved}{" "}
                      {previewScore.fields.measurementUnit}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Weight</strong>
                    </TableCell>
                    <TableCell>{previewScore.fields.weight}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Quality</strong>
                    </TableCell>
                    <TableCell>
                      {(formData.quality * 100).toFixed(0)}%
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Right Score Breakdown */}
          <Grid item xs={12} md={6}>
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 1.5, height: "100%" }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" className="text-uppercase">
                  Score Breakdown
                </Typography>

                <Stack direction="row" justifyContent="space-between">
                  <span>Base Score</span>
                  <Box component="strong">
                    {previewScore.baseScore.toFixed(2)}%
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Weighted Score</span>
                  <Box component="strong">
                    {previewScore.weightedScore.toFixed(2)}%
                  </Box>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <span>Quality Applied</span>
                  <Box component="strong">
                    {previewScore.qualityAdjusted.toFixed(2)}%
                  </Box>
                </Stack>

                <Divider />

                <Typography variant="body2">Expected Final Score</Typography>
                <div className="d-flex align-items-center">
                  <div style={{ flex: 1, marginRight: 8 }}>
                    <LinearProgress
                      variant="determinate"
                      value={previewScore.expected}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        backgroundColor: previewTints.trackColor,
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: previewTints.barColor,
                        },
                      }}
                    />
                  </div>
                  <Box
                    component="strong"
                    sx={{
                      minWidth: 72,
                      textAlign: "right",
                      color: previewTints.scoreColor,
                    }}
                  >
                    {previewScore.expected.toFixed(2)}%
                  </Box>
                </div>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <div className="d-flex align-items-center justify-content-center w-100">
          <Button variant="outlined" onClick={onClose} className="mr-3">
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={disabled}
          >
            Confirm & Submit
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmEvaluationDialog;
